import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Camera, 
  Trash2, 
  Edit, 
  Save, 
  X,
  Building,
  MapPin,
  Bed,
  Bath,
  Ruler,
  ExternalLink,
  Image as ImageIcon,
  FileText,
  Loader2
} from 'lucide-react';
import ScreenshotComparableDialog from './ScreenshotComparableDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ComparableScheduleTable({ inspection }) {
  const { token } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    if (inspection?.id) {
      fetchSchedule();
    }
  }, [inspection?.id]);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/comparables/schedule/${inspection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this comparable entry?')) return;
    
    try {
      await axios.delete(`${API}/comparables/schedule/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comparable deleted');
      fetchSchedule();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry({ ...entry });
    setShowEditDialog(true);
  };

  const saveEdit = async () => {
    try {
      await axios.put(
        `${API}/comparables/schedule/${editingEntry.id}`,
        editingEntry,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comparable updated');
      setShowEditDialog(false);
      setEditingEntry(null);
      fetchSchedule();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'Rightmove': return 'bg-purple-100 text-purple-800';
      case 'Zoopla': return 'bg-orange-100 text-orange-800';
      case 'LR': return 'bg-blue-100 text-blue-800';
      case 'Agent': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriceDisplay = (entry) => {
    if (entry.price_type === 'rent' && entry.rent_pcm) {
      return `£${entry.rent_pcm.toLocaleString()} pcm`;
    }
    if (entry.price) {
      return `£${entry.price.toLocaleString()}`;
    }
    return '-';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="comparable-schedule-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-blue-500" />
                Comparable Evidence Schedule
              </CardTitle>
              <CardDescription>
                {entries.length} comparable{entries.length !== 1 ? 's' : ''} recorded
              </CardDescription>
            </div>
            <Button 
              onClick={() => setShowScreenshotDialog(true)}
              data-testid="add-comparable-screenshot-btn"
            >
              <Camera className="h-4 w-4 mr-2" />
              Add from Screenshot
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No comparables yet</p>
              <p className="text-sm">Upload a screenshot from Rightmove, Zoopla, or Land Registry to get started</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowScreenshotDialog(true)}
              >
                <Camera className="h-4 w-4 mr-2" />
                Add Comparable from Screenshot
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Address</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Beds/Baths</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`comparable-row-${entry.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{entry.address || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{entry.postcode || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize text-sm">{entry.property_type || '-'}</span>
                        <br />
                        <span className="text-xs text-muted-foreground capitalize">{entry.tenure || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Bed className="h-3 w-3 text-muted-foreground" />
                          {entry.bedrooms ?? '-'}
                          <span className="text-muted-foreground">/</span>
                          <Bath className="h-3 w-3 text-muted-foreground" />
                          {entry.bathrooms ?? '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {entry.floor_area_sqm ? (
                          <span className="text-sm">{entry.floor_area_sqm} m²</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{getPriceDisplay(entry)}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {entry.asking_or_achieved || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.date_of_sale_or_listed || entry.date_of_transaction || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getSourceBadgeColor(entry.evidence_source)}`}>
                          {entry.evidence_source || 'Other'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {entry.distance_to_subject_km ? `${entry.distance_to_subject_km} km` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(entry)}
                            data-testid={`edit-comparable-${entry.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-comparable-${entry.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Audit Note */}
          {entries.length > 0 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
              <FileText className="h-3 w-3 inline mr-1" />
              Comparable data extracted from screenshots and reviewed by surveyor. 
              Original screenshots retained for audit purposes.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Screenshot Dialog */}
      <ScreenshotComparableDialog
        open={showScreenshotDialog}
        onClose={() => setShowScreenshotDialog(false)}
        inspection={inspection}
        onSuccess={fetchSchedule}
      />

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Comparable</DialogTitle>
            <DialogDescription>Update comparable details</DialogDescription>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Address</Label>
                  <Input
                    value={editingEntry.address || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input
                    value={editingEntry.postcode || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, postcode: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Property Type</Label>
                  <Select 
                    value={editingEntry.property_type || ''} 
                    onValueChange={(v) => setEditingEntry({ ...editingEntry, property_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bedrooms</Label>
                  <Input
                    type="number"
                    value={editingEntry.bedrooms ?? ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, bedrooms: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input
                    type="number"
                    value={editingEntry.bathrooms ?? ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, bathrooms: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
                <div>
                  <Label>Area (m²)</Label>
                  <Input
                    type="number"
                    value={editingEntry.floor_area_sqm ?? ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, floor_area_sqm: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price (£)</Label>
                  <Input
                    type="number"
                    value={editingEntry.price ?? ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, price: e.target.value ? parseFloat(e.target.value) : null })}
                  />
                </div>
                <div>
                  <Label>Price Type</Label>
                  <Select 
                    value={editingEntry.price_type || 'sale'} 
                    onValueChange={(v) => setEditingEntry({ ...editingEntry, price_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sale">Sale</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={editingEntry.asking_or_achieved || 'unknown'} 
                    onValueChange={(v) => setEditingEntry({ ...editingEntry, asking_or_achieved: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asking">Asking</SelectItem>
                      <SelectItem value="achieved">Achieved</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Condition Note</Label>
                <Input
                  value={editingEntry.condition_note || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, condition_note: e.target.value })}
                  placeholder="e.g., Recently refurbished"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
